/**
 * The crew's own schedule, as a calendar they can subscribe to.
 *
 * Jacky, 18 September: *"each crew should also have a subscription calendar that is generated, so they
 * can click and add it to calendar."*
 *
 * The address is the capability (see the `portal.installer_calendar_link` migration for why this token
 * is stored in the clear while the proposal portal hashes its own). Two things live here so that every
 * surface builds the SAME address: the path shape and the languages a crew can be sent things in.
 * A feed URL typed by hand into a phone, or texted by EmmA, has to match the route the API serves —
 * one function, four callers (the CRM editor, the portal, the command that mints it, her tool).
 */
/** What a crew may be spoken to in. The installer portal ships English and Chinese; these are its ids. */
export const CREW_LANGUAGES = ["en", "zh"];
/** A linked crew reads and writes in one language, or both — the roster stores the list. */
export const CREW_LANGUAGE_CHOICES = ["en", "zh", "en+zh"];
export function languagesFromChoice(choice) {
    if (choice === "en+zh")
        return ["en", "zh"];
    return CREW_LANGUAGES.includes(choice) ? [choice] : ["en"];
}
export function choiceFromLanguages(languages) {
    const held = new Set((languages ?? []).filter((l) => CREW_LANGUAGES.includes(l)));
    if (held.has("en") && held.has("zh"))
        return "en+zh";
    return held.has("zh") ? "zh" : "en";
}
/**
 * Where a calendar client fetches one crew's days.
 *
 * The `.ics` suffix is part of the address on purpose: Google, Apple and Outlook all decide how to
 * treat a subscription from the URL, and a person pasting it into a calendar app gets the right
 * behaviour without being told which "add by URL" screen to look for.
 */
export function crewCalendarPath(token) {
    return `/api/crew-calendar/${token}.ics`;
}
export function crewCalendarUrl(apiBase, token, pageOrigin) {
    const address = `${apiBase.replace(/\/+$/, "")}${crewCalendarPath(token)}`;
    // Browser faces use a same-origin `/api` proxy. That works for fetches, but `/api/...` alone is
    // not a URL a crew can paste into a phone. Resolve it against the face displaying the address.
    return pageOrigin && !/^https?:\/\//i.test(address) ? new URL(address, pageOrigin).toString() : address;
}
/** Tokens are minted here and nowhere else: 32 bytes of base64url, no padding, URL-safe by construction. */
export const CREW_CALENDAR_TOKEN = /^[A-Za-z0-9_-]{43}$/;
/** The feed route receives `<token>.ics`; a caller may also send the bare token. */
export function crewTokenFromFile(file) {
    const token = file.endsWith(".ics") ? file.slice(0, -4) : file;
    return CREW_CALENDAR_TOKEN.test(token) ? token : null;
}
// ── ONE CLOCK: SYDNEY ─────────────────────────────────────────────────────────
/**
 * Jacky, 18 September: *"oh and please for god sake, dont use UTC time in any system, always sydney
 * time"* — a standing rule, not a preference about formatting.
 *
 * `toISOString().slice(0, 10)` is the fault it names: that is the UTC calendar date, and for the first
 * ten hours of every Sydney day it is YESTERDAY here. A lead that arrived at 08:30 Sydney was filed as
 * the previous day's work; a crew-day booked for this morning was mirrored to the calendar as yesterday;
 * a marketing range silently started a day early. None of it looked wrong on screen, which is why it
 * survived.
 *
 * Sydney is the business's clock: the office, the crews, the suppliers and the customers are all in it.
 * So dates are Sydney dates, always, and these two functions are the way to get one. They use `en-CA`
 * because it formats as `YYYY-MM-DD` — the same shape the database columns hold.
 *
 * THE ONE EXCEPTION, and it is a specification rather than a choice: RFC 5545 requires `DTSTAMP` in a
 * calendar feed to be UTC (`...Z`). A subscribed calendar's own timezone is declared instead
 * (`X-WR-TIMEZONE:Australia/Sydney`), and every event date in the crew feed is a plain date with no time
 * and no zone. Nothing a person reads comes out in UTC.
 */
export function sydneyDay(at = new Date()) {
    return new Intl.DateTimeFormat("en-CA", {
        timeZone: "Australia/Sydney",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    }).format(at);
}
export function sydneyToday() {
    return sydneyDay(new Date());
}
/** The Sydney day an ISO instant fell on — for timestamps that arrive as strings. */
export function sydneyDayOf(iso) {
    if (!iso)
        return null;
    const at = new Date(iso);
    return Number.isFinite(at.getTime()) ? sydneyDay(at) : null;
}
const SYDNEY = "Australia/Sydney";
const parts = (at) => {
    const p = Object.fromEntries(new Intl.DateTimeFormat("en-CA", {
        timeZone: SYDNEY, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23",
    }).formatToParts(at).map((x) => [x.type, x.value]));
    return { y: +p.year, mo: +p.month, d: +p.day, h: +p.hour, mi: +p.minute, s: +p.second };
};
/** Sydney's offset from UTC at an instant, in minutes (+600 in winter, +660 in daylight saving). */
export function sydneyOffsetMinutes(at) {
    const p = parts(at);
    return Math.round((Date.UTC(p.y, p.mo - 1, p.d, p.h, p.mi, p.s) - Math.floor(at.getTime() / 1000) * 1000) / 60000);
}
/**
 * The instant at which a Sydney wall clock shows `local` (`YYYY-MM-DDTHH:mm[:ss]`, the value of an
 * `<input type="datetime-local">`). `new Date(local)` reads it in the DEVICE's zone instead — right only
 * on a Sydney device, and 18 hours out on one set to Los Angeles (audit 26 September). A wall time that
 * does not exist (02:00–02:59 on the first Sunday of October) moves forward an hour, as the clock does;
 * the repeated hour on the first Sunday of April takes its first (daylight-saving) occurrence.
 */
export function sydneyWallTimeToIso(local) {
    const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(local.trim());
    if (!m)
        return null;
    const [y, mo, d, h, mi, s] = m.slice(1).map((v) => (v === undefined ? 0 : Number(v)));
    const naive = Date.UTC(y, mo - 1, d, h, mi, s);
    // Two passes settle the offset, including on the transition days.
    let guess = naive - sydneyOffsetMinutes(new Date(naive)) * 60000;
    guess = naive - sydneyOffsetMinutes(new Date(guess)) * 60000;
    const early = naive - sydneyOffsetMinutes(new Date(guess - 3600000)) * 60000;
    const back = parts(new Date(early));
    const at = back.h === h && back.mi === mi && back.d === d ? early : guess;
    return new Date(at).toISOString();
}
/** The Sydney wall time of an instant, as `YYYY-MM-DDTHH:mm` — to prefill a datetime-local input. */
export function isoToSydneyWallTime(iso) {
    if (!iso)
        return "";
    const at = new Date(iso);
    if (!Number.isFinite(at.getTime()))
        return "";
    const p = parts(at), z = (n) => String(n).padStart(2, "0");
    return `${p.y}-${z(p.mo)}-${z(p.d)}T${z(p.h)}:${z(p.mi)}`;
}
/** The first instant of a Sydney calendar day (`YYYY-MM-DD`), as an ISO string. */
export function sydneyStartOfDay(day) {
    return sydneyWallTimeToIso(`${day}T00:00`);
}
/** A Sydney calendar day shifted by `n` days — calendar arithmetic, never 24-hour steps. */
export function shiftSydneyDay(day, n) {
    const [y, m, d] = day.split("-").map(Number);
    const t = new Date(Date.UTC(y, m - 1, d + n, 12));
    return t.toISOString().slice(0, 10);
}
/**
 * A Sydney calendar day `n` working days before `day` — weekends and the given non-working days
 * (public holidays) are skipped. A purchase order's required-by date is the installation day less the
 * configured lead (`purchase_order_defaults.lead_working_days`), so stock is in before the crew arrives.
 */
export function workingDaysBefore(day, n, nonWorking = []) {
    const off = new Set(nonWorking);
    let at = day, left = Math.max(0, Math.floor(n));
    while (left > 0) {
        at = shiftSydneyDay(at, -1);
        const [y, m, d] = at.split("-").map(Number);
        const weekday = new Date(Date.UTC(y, m - 1, d, 12)).getUTCDay();
        if (weekday !== 0 && weekday !== 6 && !off.has(at))
            left--;
    }
    return at;
}
//# sourceMappingURL=crew-calendar.js.map